import {
  take,
  call,
  cancelled,
  put,
  race,
  select,
  delay
} from "redux-saga/effects";
import pusher from "../store/Pusher.js";

import { endpointsSaga, selectEndpointByTypeSaga } from "./endpointSagas.js";
import { operationSaga } from "./operationSagas.js";

import { OPERATIONS } from "../../constants/operations.js";
import { AT_TYPES } from "../../constants/entity.js";
import { AUTHENTICATION } from "../reducers/authentication/authenticationConstants.js";
import {
  AUTH_REFRESH_THRESHOLD,
  AUTH_INACTIVITY_THRESHOLD,
  AUTH_INACTIVITY_WARNING_TIMEOUT
} from "../../constants/authenticationConstants.js";

import coreActions from "../reducers/core/coreActions.js";
import { operationAction as createOperationAction } from "../reducers/operations/operationActions.js";
import { decodeTokenPayload } from "../reducers/authentication/authenticationUtils.js";
import { createAction } from "../actions/actionUtils.js";

import {
  showInactivityWarning,
  doLogout,
  latestActivity
} from "../reducers/authentication/authenticationActions.js";

// -- SELECT QUERIES -- //

export const getExp = state => state.authentication.exp;
export const getTokens = state => state.authentication.tokens;
export const getLastActive = state => state.authentication.lastActivity;

// -- EVENT SAGAS -- //

// ============================== //
//  LOGIN SAGAS (USERNAME/PASS)   //
// ============================== //

function* loginSaga(action) {
  // console.log("loginSaga", action);
  try {
    const operationAction = createOperationAction(
      AUTHENTICATION.LOGIN,
      yield call(selectEndpointByTypeSaga, AT_TYPES.USER),
      OPERATIONS.LOGIN,
      action.payload,
      { showErrorModal: false }
    );

    const token = yield call(operationSaga, operationAction, true);

    if (token) {
      yield put(createAction(AUTHENTICATION.LOGIN.RECEIVE, token));
    }

    return token;
  } catch (err) {
    console.error("loginSaga :: Error ::", err);
  }
}

function* refreshSaga(fail) {
  // console.log("refreshSaga");
  try {
    const operationAction = createOperationAction(
      AUTHENTICATION.LOGIN,
      yield call(selectEndpointByTypeSaga, AT_TYPES.USER),
      OPERATIONS.REFRESH_LOGIN,
      undefined,
      {
        showErrorModal: false,
        logActivity: false,
        fail
      }
    );

    const token = yield call(operationSaga, operationAction, true);

    if (token) yield call(authStateChangedSaga, true);

    return token;
  } catch (err) {
    console.error("refreshSaga :: Error ::", err);
    throw err;
  }
}

function* authStateChangedSaga(doPusher = false) {
  // console.log("authStateChangedSaga");
  yield call(endpointsSaga); // refetch endpoints as permissions change at login
  yield put(coreActions.getCore());
  yield put(createAction(AUTHENTICATION.AUTHORISED));

  if (doPusher) {
    // restart pusher so that we can update the Bearer token headers
    pusher.restartPusher();
  }
}

function* logoutSaga() {
  // console.log("logoutSaga");

  // this is temporary while the operation is missing from the API return - David is on it
  try {
    const operationAction = createOperationAction(
      AUTHENTICATION.LOGOUT,
      yield call(selectEndpointByTypeSaga, AT_TYPES.USER),
      OPERATIONS.REVOKE_REFRESH,
      undefined,
      {
        autoComplete: true,
        showErrorModal: false,
        logActivity: false
      }
    );

    yield call(operationSaga, operationAction, true);
  } catch (err) {
    console.error("logoutSaga :: Error ::", err);
    // no need to throw here as SPA should always act as if logout was successful
    // in order to recover across multiple tabs
  }

  pusher.stopPusher();

  yield call(endpointsSaga); // refetch endpoints as permissions change at logout
}

// -- AUTHORISATION LOOP SAGAS -- //

function* authorizeLoopSaga(tokens) {
  // console.log("authorizeLoop:", tokens);
  if (!tokens) return null;

  try {
    // repeat the loop once it has completed
    while (true) {
      const decodedToken = decodeTokenPayload(tokens);
      const refreshDelay =
        (decodedToken.exp - decodedToken.iat) * 1000 - AUTH_REFRESH_THRESHOLD;

      yield delay(refreshDelay);

      tokens = yield call(refreshSaga);
    }
  } finally {
    if (yield cancelled()) {
      console.warn("authorizeLoop :: Cancelled");
    }
  }
}

function* inactivityLoopSaga() {
  // console.log("inactivityLoop:");
  try {
    // repeat the loop once it has completed
    while (true) {
      const lastActivity = yield select(getLastActive);

      const inactivityDelay = Math.max(
        1,
        lastActivity - Date.now() + AUTH_INACTIVITY_THRESHOLD
      );

      // race between last user activity and showing the inactivity warning
      const { showWarning } = yield race({
        lastActivityAction: take(AUTHENTICATION.LAST_ACTIVITY),
        showWarning: delay(inactivityDelay)
      });

      // show the inactivity warning
      if (showWarning) {
        yield put(showInactivityWarning());

        // race between last user activity and the warning timing out
        const { timedOut } = yield race({
          lastActivityAction: take(AUTHENTICATION.LAST_ACTIVITY),
          timedOut: delay(AUTH_INACTIVITY_WARNING_TIMEOUT)
        });

        if (timedOut) {
          yield put(doLogout());
        }
      }
    }
  } finally {
    if (yield cancelled()) {
      console.warn("inactivityLoop :: Cancelled");
    }
  }
}

// -- AUTHORISATION ROOT FLOW SAGA -- //

// used to check if an auth cookie is present on SPA startup
export function* authStartupSaga() {
  // console.log("authStartupSaga");
  const { tokens } = yield race({
    refreshFailed: take(AUTHENTICATION.STARTUP_FAILED),
    tokens: call(refreshSaga, createAction(AUTHENTICATION.STARTUP_FAILED))
  });

  if (tokens) {
    pusher.startPusher();
    yield put(createAction(AUTHENTICATION.STARTUP_SUCCESS));
  }

  return tokens || null;
}

// complete authentication flow
export function* authFlowSaga() {
  // console.log("authFlowSaga");

  // repeat the flow once it has completed
  while (true) {
    let storedTokens = yield select(getTokens);
    let token = storedTokens ? storedTokens.token : null;

    // we have no refresh token so wait for login action
    if (!token) {
      const { loginReceived, loginAction } = yield race({
        loginReceived: take(AUTHENTICATION.LOGIN.RECEIVE),
        loginAction: take(AUTHENTICATION.LOGIN.DO)
      });

      if (loginReceived) {
        storedTokens = loginReceived.payload;
      }

      if (loginAction) {
        storedTokens = yield call(loginSaga, loginAction);
      }

      if (storedTokens) {
        yield put(latestActivity());
        yield call(authStateChangedSaga, true);
        pusher.startPusher();
      }
    } else {
      // need to reset activity as this is likely an autologin on browser refresh
      yield put(latestActivity());
    }

    const { logoutAction } = yield race({
      logoutAction: take([
        AUTHENTICATION.LOGIN.FAILURE,
        AUTHENTICATION.LOGOUT.DO
      ]),
      authLoop: call(authorizeLoopSaga, storedTokens),
      inactivityLoop: call(inactivityLoopSaga)
    });

    if (logoutAction) {
      yield call(logoutSaga);
    }
  }
}
